import React, { useState, useEffect } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { letterTemplateService } from '../../services/letterTemplateService';
import type { LetterTemplate } from '../../types/letterTemplate.types';
import { LETTER_CATEGORIES } from '../../types/letterTemplate.types';

interface LetterTemplateSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (template: LetterTemplate) => void;
}

const LetterTemplateSelector: React.FC<LetterTemplateSelectorProps> = ({ isOpen, onClose, onSelect }) => {
  const [templates, setTemplates] = useState<LetterTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadTemplates();
    }
  }, [isOpen]);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const result = await letterTemplateService.getTemplates({
        search: search || undefined,
        category: categoryFilter || undefined,
        sortBy: 'usageCount',
        sortOrder: 'DESC',
        limit: 50,
      });
      setTemplates(result.templates);
    } catch (error) {
      console.error('Failed to load letter templates:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(loadTemplates, 300);
      return () => clearTimeout(timer);
    }
  }, [search, categoryFilter]);

  const selectedTemplate = templates.find(t => t.id === selectedId);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Select Letter Template</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-3 p-4 border-b border-gray-200 dark:border-gray-700">
          <input
            type="text"
            placeholder="Search templates..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
          />
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-200"
          >
            <option value="">All Categories</option>
            {LETTER_CATEGORIES.map((cat) => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex min-h-0">
          {/* Template List */}
          <div className="w-1/2 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center p-8">
                <svg className="w-6 h-6 animate-spin text-blue-500" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
            ) : templates.length === 0 ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <p className="mb-2">No letter templates found.</p>
                <p className="text-sm">Upload templates from the Templates page.</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {templates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => setSelectedId(template.id)}
                    className={`w-full text-left p-4 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                      selectedId === template.id ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-500' : ''
                    }`}
                  >
                    <div className="font-medium text-gray-900 dark:text-white text-sm">{template.title}</div>
                    {template.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">{template.description}</div>
                    )}
                    <div className="flex items-center gap-2 mt-2">
                      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                        {template.category}
                      </span>
                      {template.originalFileType && (
                        <span className="text-xs text-gray-400">
                          from .{template.originalFileType}
                        </span>
                      )}
                      {template.detectedVariables && template.detectedVariables.length > 0 && (
                        <span className="text-xs text-blue-500">
                          {template.detectedVariables.length} variable{template.detectedVariables.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Preview Panel */}
          <div className="w-1/2 overflow-y-auto p-4">
            {selectedTemplate ? (
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{selectedTemplate.title}</h3>
                {selectedTemplate.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{selectedTemplate.description}</p>
                )}
                {selectedTemplate.detectedVariables && selectedTemplate.detectedVariables.length > 0 && (
                  <div className="mb-3">
                    <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Variables</h4>
                    <div className="flex flex-wrap gap-1">
                      {selectedTemplate.detectedVariables.map((v) => (
                        <span key={v} className="text-xs px-2 py-0.5 rounded bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300">
                          {'{' + v + '}'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-900 text-sm max-h-64 overflow-y-auto">
                  <div
                    dangerouslySetInnerHTML={{ __html: sanitizeHtml(selectedTemplate.htmlContent.substring(0, 2000)) }}
                    className="prose prose-sm dark:prose-invert max-w-none"
                  />
                  {selectedTemplate.htmlContent.length > 2000 && (
                    <p className="text-gray-400 mt-2 italic">...preview truncated</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-400 dark:text-gray-500 text-sm">
                Select a template to preview
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (selectedTemplate) {
                onSelect(selectedTemplate);
              }
            }}
            disabled={!selectedTemplate}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Select Template
          </button>
        </div>
      </div>
    </div>
  );
};

export default LetterTemplateSelector;
